import { ChevronDown, ChevronUp, Search } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useRfpQuestionFindAnswers } from "../../hooks/useRfpQuestionFindAnswers";
import { fetchKbQuestionDetail, fetchKbQuestions } from "../../services/kbQuestionsService";
import { updateRfpQuestionAnswer } from "../../services/rfpsService";
import type { KbQuestionItem } from "../../types/kbQuestion";
import type { RfpQuestionFindAnswerResult } from "../../types/rfp";
import { Drawer } from "./Drawer";

type LibraryAnswer = {
  readonly id: string;
  readonly title: string;
  readonly source: string;
  readonly answer: string;
};

type RfpFindAnswerDrawerProps = {
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly rfpId: string | null;
  readonly questionId: string | null;
  readonly questionText: string;
  readonly onApplySuccess?: () => void;
};

type LoadingSpinnerProps = {
  readonly className?: string;
};

const LoadingSpinner = ({ className = "h-8 w-8" }: LoadingSpinnerProps) => (
  <svg className={`${className} animate-spin text-primary-500`} viewBox="0 0 24 24" fill="none" aria-hidden>
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
  </svg>
);

const toLibraryAnswer = (result: RfpQuestionFindAnswerResult, index: number): LibraryAnswer => {
  const id = result.answer_id || result.question_id || result.doc_id || result.base_id || `answer-${index}`;
  const title = result.matched_question?.trim() || "Matched answer";
  const source = result.section?.trim() || "Knowledge Base";
  const answer = result.retrieved_answer?.trim() || result.retrieved_response?.trim() || "";
  return { id, title, source, answer };
};

export const RfpFindAnswerDrawer = ({
  isOpen,
  onClose,
  rfpId,
  questionId,
  questionText,
  onApplySuccess,
}: RfpFindAnswerDrawerProps) => {
  const [isAddNewAnswerOpen, setIsAddNewAnswerOpen] = useState(false);
  const [customAnswer, setCustomAnswer] = useState("");
  const [expandedItemId, setExpandedItemId] = useState<string>("");
  const [libraryAnswerValues, setLibraryAnswerValues] = useState<Record<string, string>>({});
  const [isApplyingAddNew, setIsApplyingAddNew] = useState(false);
  const [applyingLibraryAnswerId, setApplyingLibraryAnswerId] = useState<string | null>(null);
  const [applyError, setApplyError] = useState<string | null>(null);
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearchInput, setDebouncedSearchInput] = useState("");
  const [kbSearchResults, setKbSearchResults] = useState<readonly KbQuestionItem[]>([]);
  const [kbSearchLoading, setKbSearchLoading] = useState(false);
  const [kbSearchError, setKbSearchError] = useState<string | null>(null);
  const [kbSearchRetryToken, setKbSearchRetryToken] = useState(0);
  const latestKbSearchRequestRef = useRef(0);
  const normalizedRfpId = rfpId?.trim() ?? "";
  const normalizedQuestionId = questionId?.trim() ?? "";
  const drawerContextKey = `${normalizedRfpId}:${normalizedQuestionId}`;
  const { findAnswers, loading, error, loadFindAnswers } = useRfpQuestionFindAnswers({
    rfpId,
    questionId,
    isOpen,
    topN: 5,
  });
  const libraryAnswers = useMemo(
    () => (findAnswers?.results ?? []).map(toLibraryAnswer),
    [findAnswers?.results],
  );
  const hasResults = libraryAnswers.length > 0;
  const isSearchMode = searchInput.trim().length > 0;

  useEffect(() => {
    if (!isOpen) return;
    setIsAddNewAnswerOpen(false);
    setCustomAnswer("");
    setExpandedItemId("");
    setLibraryAnswerValues({});
    setIsApplyingAddNew(false);
    setApplyingLibraryAnswerId(null);
    setApplyError(null);
    setSearchInput("");
    setDebouncedSearchInput("");
    setKbSearchResults([]);
    setKbSearchLoading(false);
    setKbSearchError(null);
    setKbSearchRetryToken(0);
  }, [drawerContextKey, isOpen]);

  useEffect(() => {
    const handle = window.setTimeout(() => {
      setDebouncedSearchInput(searchInput.trim());
    }, 400);

    return () => {
      window.clearTimeout(handle);
    };
  }, [searchInput]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    if (!debouncedSearchInput) {
      setKbSearchResults([]);
      setKbSearchLoading(false);
      setKbSearchError(null);
      return;
    }

    const requestId = latestKbSearchRequestRef.current + 1;
    latestKbSearchRequestRef.current = requestId;

    setKbSearchLoading(true);
    setKbSearchError(null);

    void (async () => {
      try {
        const response = await fetchKbQuestions({
          page: 1,
          page_size: 10,
          sort_by: "created_at",
          sort_dir: "desc",
          search: debouncedSearchInput,
        });
        if (latestKbSearchRequestRef.current !== requestId) {
          return;
        }
        setKbSearchResults(response.data ?? []);
      } catch (e) {
        if (latestKbSearchRequestRef.current !== requestId) {
          return;
        }
        const message = e instanceof Error ? e.message : "Failed to load library search results";
        setKbSearchError(message);
        setKbSearchResults([]);
      } finally {
        if (latestKbSearchRequestRef.current === requestId) {
          setKbSearchLoading(false);
        }
      }
    })();
  }, [debouncedSearchInput, isOpen, kbSearchRetryToken]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const nextExpandedId = libraryAnswers[0]?.id ?? "";
    setExpandedItemId(nextExpandedId);
    setLibraryAnswerValues(Object.fromEntries(libraryAnswers.map((item) => [item.id, item.answer])));
  }, [isOpen, libraryAnswers]);

  const applyManualAnswer = async (answerText: string): Promise<void> => {
    if (!normalizedRfpId || !normalizedQuestionId) {
      throw new Error("Missing RFP or question context.");
    }
    await updateRfpQuestionAnswer(normalizedRfpId, normalizedQuestionId, {
      answer_text: answerText,
      answer_type: "manual",
    });
  };

  const handleApplyAddNewAnswer = async (): Promise<void> => {
    if (isApplyingAddNew || applyingLibraryAnswerId) return;

    setApplyError(null);
    setIsApplyingAddNew(true);
    try {
      await applyManualAnswer(customAnswer.trim());
      onApplySuccess?.();
    } catch (e) {
      const message = e instanceof Error ? e.message : "Failed to apply answer";
      setApplyError(message);
    } finally {
      setIsApplyingAddNew(false);
    }
  };

  const handleApplyLibraryAnswer = async (itemId: string): Promise<void> => {
    if (isApplyingAddNew || applyingLibraryAnswerId) return;

    const answerText = (libraryAnswerValues[itemId] ?? "").trim();
    setApplyError(null);
    setApplyingLibraryAnswerId(itemId);
    try {
      await applyManualAnswer(answerText);
      onApplySuccess?.();
    } catch (e) {
      const message = e instanceof Error ? e.message : "Failed to apply answer";
      setApplyError(message);
    } finally {
      setApplyingLibraryAnswerId(null);
    }
  };

  const handleApplySearchResult = async (kbQuestionId: string): Promise<void> => {
    if (isApplyingAddNew || applyingLibraryAnswerId) return;

    const applyKey = `search-${kbQuestionId}`;
    setApplyError(null);
    setApplyingLibraryAnswerId(applyKey);
    try {
      const detail = await fetchKbQuestionDetail(kbQuestionId);
      const currentVersion =
        detail.answer_versions.find((version) => version.is_current) ??
        [...detail.answer_versions].sort((a, b) => b.version - a.version)[0];
      const answerText = currentVersion?.answer_text?.trim() ?? "";

      if (!answerText) {
        throw new Error("Selected library question has no current answer to apply.");
      }

      await applyManualAnswer(answerText);
      onApplySuccess?.();
    } catch (e) {
      const message = e instanceof Error ? e.message : "Failed to apply answer";
      setApplyError(message);
    } finally {
      setApplyingLibraryAnswerId(null);
    }
  };

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      title="Update Answer from Library"
      panelClassName="max-w-[480px]"
      className="flex flex-1 flex-col p-0"
    >
      <div className="flex-1 overflow-y-auto p-5">
        <p className="rounded border border-gray-200 bg-gray-50 p-3 text-sm leading-6 text-gray-500">
          {questionText}
        </p>

        <div className="mt-4">
          <label
            htmlFor="library-search-input"
            className="mb-2 block text-xs font-semibold uppercase tracking-wide text-gray-500"
          >
            Search Library Questions & Answers
          </label>
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <input
              id="library-search-input"
              type="text"
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
              placeholder="Search question or answer..."
              className="w-full rounded border border-gray-200 bg-white py-2 pl-9 pr-3 text-sm text-slate-700 outline-none transition-all focus:border-primary-500 focus:shadow-[0_0_0_3px_rgba(0,156,229,0.1)]"
            />
          </div>
        </div>

        <button
          type="button"
          onClick={() => setIsAddNewAnswerOpen((value) => !value)}
          className={`mt-4 w-full rounded border px-4 py-2.5 text-sm font-medium transition-colors ${
            isAddNewAnswerOpen
              ? "border-primary-500 bg-sky-50 text-primary-600"
              : "border-gray-200 bg-white text-slate-700 hover:bg-gray-50"
          }`}
        >
          {isAddNewAnswerOpen ? "Hide Add New Answer" : "Add New Answer"}
        </button>

        {isAddNewAnswerOpen ? (
          <div className="mt-4 rounded-lg border border-primary-500 bg-sky-50 p-4">
            <label htmlFor="new-library-answer" className="mb-2 block text-sm font-semibold text-gray-800">
              Type Your New Answer
            </label>
            <textarea
              id="new-library-answer"
              value={customAnswer}
              onChange={(event) => setCustomAnswer(event.target.value)}
              rows={8}
              placeholder="Enter your custom answer here..."
              className="w-full resize-y rounded border border-primary-500 bg-white p-3 text-sm leading-6 text-slate-700 outline-none transition-all focus:shadow-[0_0_0_3px_rgba(0,156,229,0.1)]"
            />
            <div className="mt-3 flex justify-end">
              <button
                type="button"
                onClick={() => void handleApplyAddNewAnswer()}
                disabled={!customAnswer.trim() || isApplyingAddNew || applyingLibraryAnswerId !== null}
                className="rounded bg-primary-500 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:bg-gray-300"
              >
                {isApplyingAddNew ? "Applying..." : "Apply"}
              </button>
            </div>
          </div>
        ) : null}

        <div className="mt-4 space-y-3">
          {applyError ? (
            <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              {applyError}
            </div>
          ) : null}
          {isSearchMode && kbSearchLoading ? (
            <div className="flex items-center justify-center gap-2 rounded-lg border border-gray-200 bg-white px-4 py-10 text-sm text-gray-500">
              <LoadingSpinner className="h-5 w-5" />
              Searching library...
            </div>
          ) : null}
          {isSearchMode && !kbSearchLoading && kbSearchError ? (
            <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              <p>{kbSearchError}</p>
              <button
                type="button"
                onClick={() => setKbSearchRetryToken((value) => value + 1)}
                className="mt-3 rounded border border-red-200 bg-white px-3 py-1.5 text-xs font-medium text-red-700"
              >
                Retry
              </button>
            </div>
          ) : null}
          {isSearchMode && !kbSearchLoading && !kbSearchError && kbSearchResults.length === 0 ? (
            <div className="rounded-lg border border-gray-200 bg-white px-4 py-10 text-center text-sm text-gray-500">
              No matching questions found in the knowledge base.
            </div>
          ) : null}
          {isSearchMode && !kbSearchLoading && !kbSearchError
            ? kbSearchResults.map((item) => {
                const applyKey = `search-${item.id}`;
                return (
                  <div
                    key={item.id}
                    className="rounded-lg border border-gray-200 bg-white px-4 py-3"
                  >
                    <p className="text-sm font-semibold text-gray-800">{item.question_text}</p>
                    <p className="mt-1 text-xs text-gray-500">
                      {item.section_name?.trim() || "Knowledge Base"}
                    </p>
                    <p className="mt-2 whitespace-pre-line text-sm leading-6 text-gray-700">
                      {item.current_answer_preview?.trim() || "No answer preview available."}
                    </p>
                    <div className="mt-3 flex justify-end">
                      <button
                        type="button"
                        onClick={() => void handleApplySearchResult(item.id)}
                        disabled={isApplyingAddNew || applyingLibraryAnswerId !== null}
                        className="rounded bg-primary-500 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:bg-gray-300"
                      >
                        {applyingLibraryAnswerId === applyKey ? "Applying..." : "Apply"}
                      </button>
                    </div>
                  </div>
                );
              })
            : null}
          {!isSearchMode && loading ? (
            <div className="flex items-center justify-center gap-2 rounded-lg border border-gray-200 bg-white px-4 py-10 text-sm text-gray-500">
              <LoadingSpinner className="h-5 w-5" />
              Loading answers...
            </div>
          ) : null}
          {!isSearchMode && !loading && error ? (
            <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              <p>{error}</p>
              <button
                type="button"
                onClick={() => void loadFindAnswers()}
                className="mt-3 rounded border border-red-200 bg-white px-3 py-1.5 text-xs font-medium text-red-700"
              >
                Retry
              </button>
            </div>
          ) : null}
          {!isSearchMode && !loading && !error && !normalizedQuestionId ? (
            <div className="rounded-lg border border-gray-200 bg-white px-4 py-10 text-center text-sm text-gray-500">
              Select a question to find related answers.
            </div>
          ) : null}
          {!isSearchMode && !loading && !error && normalizedQuestionId && !hasResults ? (
            <div className="rounded-lg border border-gray-200 bg-white px-4 py-10 text-center text-sm text-gray-500">
              No related answers found in the knowledge base.
            </div>
          ) : null}
          {!isSearchMode && !loading && !error
            ? libraryAnswers.map((item) => {
            const isExpanded = expandedItemId === item.id;
            return (
              <div
                key={item.id}
                className={`overflow-hidden rounded-lg border transition-all ${
                  isExpanded ? "border-primary-500 bg-slate-100" : "border-gray-200 bg-white"
                }`}
              >
                <button
                  type="button"
                  onClick={() => setExpandedItemId((current) => (current === item.id ? "" : item.id))}
                  className="flex w-full items-center justify-between px-4 py-3 text-left"
                >
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-gray-800">{item.title}</p>
                    <p className="text-xs text-gray-500">{item.source}</p>
                  </div>
                  {isExpanded ? (
                    <ChevronUp className="h-4 w-4 shrink-0 text-gray-500" />
                  ) : (
                    <ChevronDown className="h-4 w-4 shrink-0 text-gray-500" />
                  )}
                </button>
                {isExpanded ? (
                  <div className="px-4 pb-4 pt-2">
                    <textarea
                      rows={8}
                      value={libraryAnswerValues[item.id] ?? item.answer}
                      onChange={(event) =>
                        setLibraryAnswerValues((current) => ({
                          ...current,
                          [item.id]: event.target.value,
                        }))
                      }
                      className="w-full resize-y rounded border border-primary-500 bg-white p-3 text-sm leading-6 text-slate-700 outline-none transition-all focus:shadow-[0_0_0_3px_rgba(0,156,229,0.1)]"
                    />
                    <div className="mt-3 flex justify-end">
                      <button
                        type="button"
                        onClick={() => void handleApplyLibraryAnswer(item.id)}
                        disabled={
                          !(libraryAnswerValues[item.id] ?? "").trim() ||
                          isApplyingAddNew ||
                          applyingLibraryAnswerId !== null
                        }
                        className="rounded bg-primary-500 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:bg-gray-300"
                      >
                        {applyingLibraryAnswerId === item.id ? "Applying..." : "Apply"}
                      </button>
                    </div>
                  </div>
                ) : null}
              </div>
            );
            })
            : null}
        </div>
      </div>

      <div className="border-t border-gray-200 bg-white p-5">
        <div className="flex">
          <button
            type="button"
            onClick={onClose}
            className="w-full rounded border border-gray-200 px-4 py-2.5 text-sm font-medium text-gray-500 transition-colors hover:bg-gray-100"
          >
            Cancel
          </button>
        </div>
      </div>
    </Drawer>
  );
};
